import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  CheckCircle,
  Circle,
  Play,
  Cloud,
  Database,
  Shield,
  Server,
  Brain,
  Lock,
  FileSearch,
  Workflow,
  BarChart3,
  Users,
  Globe,
  Zap,
  ChevronDown,
  ChevronUp,
  ExternalLink,
  Layers,
  RotateCcw,
  AlertTriangle,
} from 'lucide-react';
import { apiClient } from '../api/client';

// Demo sections matching demo-outline.md
const demoSections = [
  {
    id: 1,
    title: 'Introduction & Overview',
    status: 'completed',
    description: 'Platform overview, Azure stack, and 9 demo personas',
    features: ['Demo Hub', 'Azure stack overview', '9 personas across 5 roles', 'Custom domain'],
    route: '/demo-hub',
  },
  {
    id: 2,
    title: 'Evidence Intake & Chain of Custody',
    status: 'completed',
    description: 'Secure intake with SHA-256 hashing, two-step approval, and public submission',
    features: ['Law enforcement upload', 'Public portal', 'SHA-256 hashing', 'WORM immutability', 'Accept/Reject workflow', 'Accept & Activate'],
    route: '/evidence/upload',
  },
  {
    id: 3,
    title: 'Evidence Review & AI Analysis',
    status: 'completed',
    description: 'Agent Aegis AI, Video Intelligence, Charge History, Document Intelligence',
    features: ['Agent Aegis (GPT-4o)', 'Video Intelligence', 'Charge History extraction', 'Document Intelligence', 'Hash verification', 'Word export'],
    route: '/evidence',
  },
  {
    id: 4,
    title: 'Annotations, Redaction & Pre-Filing',
    status: 'completed',
    description: 'Live annotation with 5 tools, canvas redaction with finalize, PII scanner',
    features: ['5 annotation tools', '5 colors', 'Rectangle fill/outline', 'Private by default', 'Canvas redaction', 'Finalize redacted copy', 'PII Scanner'],
    route: '/evidence',
  },
  {
    id: 5,
    title: 'Court Filing & CMS Integration',
    status: 'completed',
    description: 'Exhibit creation, filing validation, and court acknowledgment',
    features: ['Exhibit creation', 'Filing validation', 'Court acknowledgment', 'Pending filings queue'],
    route: '/filing',
  },
  {
    id: 6,
    title: 'Discovery & Collaboration',
    status: 'completed',
    description: 'Secure sharing with access codes, expiration, and full audit trail',
    features: ['View/Download/Full sharing', 'Access codes', 'Expiration dates', 'Access tracking', 'Brady compliance'],
    route: '/discovery',
  },
  {
    id: 7,
    title: 'Courtroom Presentation',
    status: 'completed',
    description: 'Fullscreen evidence with live annotation, keyboard nav, and SignalR court sharing',
    features: ['Fullscreen mode', 'Keyboard shortcuts', 'Live annotation overlay', 'Court sharing via SignalR', 'Acknowledge/Dismiss', 'Exhibit queue'],
    route: '/courtroom',
  },
  {
    id: 8,
    title: 'Disposition & Retention',
    status: 'completed',
    description: 'Legal holds with separation of duties, retention policies, and archive workflow',
    features: ['Legal holds', '7yr criminal / 3yr civil', 'Separation of duties', 'Archive workflow', 'Disposal guards', 'Full audit trail'],
    route: '/disposition',
  },
  {
    id: 9,
    title: 'Security & Compliance',
    status: 'completed',
    description: 'RBAC enforcement, compliance dashboards, and complete audit logging',
    features: ['5 RBAC roles', 'CJIS compliance', 'FedRAMP alignment', 'NIST controls', 'Audit log', 'Role-based navigation'],
    route: '/compliance',
  },
  {
    id: 10,
    title: 'AI Agents & Architecture',
    status: 'completed',
    description: '5 AI agents, interactive architecture diagrams, and technical deep-dive',
    features: ['5 AI agents', 'Discovery Copilot', 'Integrity Monitor', 'Interactive Mermaid diagrams', 'Azure service topology'],
    route: '/ai-agents',
  },
];

// Microsoft Azure stack - current implementation
const currentStack = [
  {
    category: 'Compute & API',
    icon: Server,
    color: 'bg-blue-500',
    products: [
      { name: 'Azure Functions', description: 'Serverless API backend (Node.js 22)', status: 'deployed' },
      { name: 'Azure Static Web Apps', description: 'React frontend with custom domain', status: 'deployed' },
    ],
  },
  {
    category: 'Data & Storage',
    icon: Database,
    color: 'bg-green-500',
    products: [
      { name: 'Azure SQL Database', description: 'Evidence metadata & chain of custody', status: 'deployed' },
      { name: 'Azure Blob Storage', description: 'Evidence file storage with WORM immutability', status: 'deployed' },
    ],
  },
  {
    category: 'AI & Intelligence',
    icon: Brain,
    color: 'bg-pink-500',
    products: [
      { name: 'Azure OpenAI (GPT-4o)', description: 'Agent Aegis content understanding', status: 'deployed' },
      { name: 'Azure Content Understanding', description: 'Multi-modal evidence analysis', status: 'deployed' },
      { name: 'Azure Video Indexer', description: 'Video analysis (Advanced preset)', status: 'deployed' },
      { name: 'Azure Document Intelligence', description: 'OCR, tables, & entity extraction', status: 'deployed' },
      { name: 'Azure AI Language', description: 'PII detection & NER', status: 'deployed' },
    ],
  },
  {
    category: 'Identity & Security',
    icon: Shield,
    color: 'bg-purple-500',
    products: [
      { name: 'Microsoft Entra ID', description: 'SSO & MFA authentication', status: 'deployed' },
      { name: 'Azure Key Vault', description: 'Secrets management', status: 'deployed' },
      { name: 'Azure RBAC', description: 'Role-based access control', status: 'deployed' },
    ],
  },
  {
    category: 'Real-Time & Monitoring',
    icon: BarChart3,
    color: 'bg-orange-500',
    products: [
      { name: 'Azure SignalR Service', description: 'Real-time court sharing & notifications', status: 'deployed' },
      { name: 'Azure Monitor', description: 'Application performance monitoring', status: 'deployed' },
      { name: 'Log Analytics', description: 'Centralized logging & KQL queries', status: 'deployed' },
    ],
  },
];

// Potential expansion products
const expansionStack = [
  {
    category: 'Search & Discovery',
    icon: FileSearch,
    color: 'bg-cyan-500',
    products: [
      { name: 'Azure AI Search', description: 'Full-text & vector search across evidence', potential: 'high' },
      { name: 'Microsoft Purview', description: 'Data governance & compliance scanning', potential: 'medium' },
    ],
  },
  {
    category: 'Integration & Workflow',
    icon: Workflow,
    color: 'bg-indigo-500',
    products: [
      { name: 'Azure Logic Apps', description: 'Workflow automation & CMS integration', potential: 'high' },
      { name: 'Power Automate', description: 'No-code workflow triggers', potential: 'medium' },
      { name: 'Power Apps', description: 'Custom intake forms & mobile apps', potential: 'medium' },
      { name: 'Azure Service Bus', description: 'Event-driven architecture', potential: 'low' },
    ],
  },
  {
    category: 'Advanced Security',
    icon: Lock,
    color: 'bg-red-500',
    products: [
      { name: 'Microsoft Defender for Cloud', description: 'Threat protection & security posture', potential: 'high' },
      { name: 'Azure Confidential Computing', description: 'Encrypted evidence processing', potential: 'medium' },
      { name: 'Azure Private Link', description: 'Private network access', potential: 'medium' },
    ],
  },
  {
    category: 'Global Scale',
    icon: Globe,
    color: 'bg-teal-500',
    products: [
      { name: 'Azure Front Door', description: 'Global load balancing & CDN', potential: 'medium' },
      { name: 'Azure Cosmos DB', description: 'Multi-region evidence metadata', potential: 'low' },
    ],
  },
];

export default function DemoHub() {
  const { user } = useAuth();
  const [expandedSection, setExpandedSection] = useState<number | null>(null);
  const [showExpansion, setShowExpansion] = useState(false);
  const [resetState, setResetState] = useState<'idle' | 'confirm' | 'loading' | 'success' | 'error'>('idle');
  const [resetResult, setResetResult] = useState<{ message: string; counts?: { cases: number; evidence: number; custodyEvents: number } } | null>(null);
  const [backfillState, setBackfillState] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [backfillResult, setBackfillResult] = useState<{ total: number; processed: number; failed: number } | null>(null);

  const handleBackfill = async () => {
    setBackfillState('loading');
    try {
      const result = await apiClient.extractAllEvidence();
      if (result.success) {
        setBackfillResult(result.results);
        setBackfillState('success');
      } else {
        setBackfillState('error');
      }
    } catch (err: unknown) {
      setBackfillState('error');
    }
  };

  const handleDemoReset = async () => {
    setResetState('loading');
    try {
      const result = await apiClient.resetDemoData();
      if (result.success) {
        setResetResult({ message: result.message, counts: result.counts });
        setResetState('success');
      } else {
        setResetResult({ message: result.message });
        setResetState('error');
      }
    } catch (err: unknown) {
      setResetResult({ message: err instanceof Error ? err.message : 'Reset failed. Check network and try again.' });
      setResetState('error');
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="w-6 h-6 text-green-500" />;
      case 'in-progress':
        return <Play className="w-6 h-6 text-blue-500 animate-pulse" />;
      default:
        return <Circle className="w-6 h-6 text-gray-300" />;
    }
  };

  const getPotentialBadge = (potential: string) => {
    switch (potential) {
      case 'high':
        return <span className="px-2 py-0.5 text-xs rounded-full bg-green-100 text-green-800">High Value</span>;
      case 'medium':
        return <span className="px-2 py-0.5 text-xs rounded-full bg-yellow-100 text-yellow-800">Medium</span>;
      default:
        return <span className="px-2 py-0.5 text-xs rounded-full bg-gray-100 text-gray-600">Future</span>;
    }
  };

  return (
    <div className="max-w-7xl mx-auto p-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-indigo-700 rounded-xl p-8 text-white mb-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold mb-2">DEMS Demo Hub</h1>
            <p className="text-blue-100">
              Digital Evidence Management System • Powered by Microsoft Azure
            </p>
            <Link
              to="/diagrams"
              className="inline-flex items-center gap-2 mt-4 px-4 py-2 bg-white/10 hover:bg-white/20 rounded-lg transition-colors text-sm font-medium"
            >
              <Layers className="w-4 h-4" />
              View Architecture Diagrams
            </Link>
          </div>
          <div className="text-right">
            <div className="text-sm text-blue-200">Logged in as</div>
            <div className="text-lg font-semibold">{user?.name || 'Not logged in'}</div>
            <div className="text-sm text-blue-200">{user?.role || ''}</div>
          </div>
        </div>
        
        {/* Quick stats bar */}
        <div className="mt-6 flex gap-6 text-sm text-blue-100">
          <span>{demoSections.length} demo sections</span>
          <span>·</span>
          <span>9 personas across 5 roles</span>
          <span>·</span>
          <span>{currentStack.reduce((acc, cat) => acc + cat.products.length, 0)} Azure services deployed</span>
        </div>
      </div>

      {/* Two column layout */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Left: Demo Sections */}
        <div>
          <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
            <Play className="w-5 h-5 text-blue-600" />
            Demo Sections
          </h2>
          
          <div className="space-y-3">
            {demoSections.map((section) => (
              <div
                key={section.id}
                className={`bg-white rounded-lg shadow border-l-4 ${
                  section.status === 'completed' ? 'border-green-500' :
                  section.status === 'in-progress' ? 'border-blue-500' :
                  'border-gray-300'
                }`}
              >
                <button
                  onClick={() => setExpandedSection(expandedSection === section.id ? null : section.id)}
                  className="w-full p-4 flex items-center justify-between text-left"
                >
                  <div className="flex items-center gap-3">
                    {getStatusIcon(section.status)}
                    <div>
                      <div className="font-medium">
                        {section.id}. {section.title}
                      </div>
                      <div className="text-sm text-gray-500">{section.description}</div>
                    </div>
                  </div>
                  {expandedSection === section.id ? (
                    <ChevronUp className="w-5 h-5 text-gray-400" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-gray-400" />
                  )}
                </button>
                
                {expandedSection === section.id && (
                  <div className="px-4 pb-4 border-t">
                    <div className="pt-3">
                      <div className="text-sm text-gray-600 mb-2">Features:</div>
                      <div className="flex flex-wrap gap-2">
                        {section.features.map((feature, i) => (
                          <span key={i} className="px-2 py-1 bg-gray-100 rounded text-sm">
                            {feature}
                          </span>
                        ))}
                      </div>
                      {section.route && (
                        <a
                          href={section.route}
                          className="mt-3 inline-flex items-center gap-1 text-blue-600 hover:text-blue-800 text-sm"
                        >
                          Open in app <ExternalLink className="w-4 h-4" />
                        </a>
                      )}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Right: Microsoft Stack */}
        <div>
          <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
            <Cloud className="w-5 h-5 text-blue-600" />
            Microsoft Azure Stack
          </h2>

          {/* Current Implementation */}
          <div className="bg-white rounded-lg shadow p-4 mb-4">
            <h3 className="font-semibold text-green-700 mb-3 flex items-center gap-2">
              <Zap className="w-4 h-4" />
              Currently Deployed
            </h3>
            <div className="space-y-4">
              {currentStack.map((category) => (
                <div key={category.category}>
                  <div className="flex items-center gap-2 mb-2">
                    <div className={`p-1.5 rounded ${category.color}`}>
                      <category.icon className="w-4 h-4 text-white" />
                    </div>
                    <span className="font-medium text-sm">{category.category}</span>
                  </div>
                  <div className="ml-8 space-y-1">
                    {category.products.map((product) => (
                      <div key={product.name} className="flex items-center justify-between text-sm">
                        <span>{product.name}</span>
                        <span className="text-gray-500 text-xs">{product.description}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Expansion Toggle */}
          <button
            onClick={() => setShowExpansion(!showExpansion)}
            className="w-full bg-gradient-to-r from-purple-50 to-pink-50 border border-purple-200 rounded-lg p-4 mb-4 flex items-center justify-between hover:from-purple-100 hover:to-pink-100 transition-colors"
          >
            <div className="flex items-center gap-2">
              <Brain className="w-5 h-5 text-purple-600" />
              <span className="font-medium text-purple-900">Expansion Potential</span>
              <span className="text-sm text-purple-600">({expansionStack.reduce((acc, cat) => acc + cat.products.length, 0)} services)</span>
            </div>
            {showExpansion ? (
              <ChevronUp className="w-5 h-5 text-purple-400" />
            ) : (
              <ChevronDown className="w-5 h-5 text-purple-400" />
            )}
          </button>

          {/* Expansion Products */}
          {showExpansion && (
            <div className="bg-white rounded-lg shadow p-4 space-y-4">
              {expansionStack.map((category) => (
                <div key={category.category}>
                  <div className="flex items-center gap-2 mb-2">
                    <div className={`p-1.5 rounded ${category.color}`}>
                      <category.icon className="w-4 h-4 text-white" />
                    </div>
                    <span className="font-medium text-sm">{category.category}</span>
                  </div>
                  <div className="ml-8 space-y-2">
                    {category.products.map((product) => (
                      <div key={product.name} className="flex items-center justify-between">
                        <div>
                          <div className="text-sm font-medium">{product.name}</div>
                          <div className="text-xs text-gray-500">{product.description}</div>
                        </div>
                        {getPotentialBadge(product.potential)}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Quick Stats */}
          <div className="grid grid-cols-3 gap-3 mt-4">
            <div className="bg-blue-50 rounded-lg p-3 text-center">
              <div className="text-2xl font-bold text-blue-600">9</div>
              <div className="text-xs text-blue-800">Demo Personas</div>
            </div>
            <div className="bg-green-50 rounded-lg p-3 text-center">
              <div className="text-2xl font-bold text-green-600">{currentStack.reduce((acc, cat) => acc + cat.products.length, 0)}</div>
              <div className="text-xs text-green-800">Azure Services</div>
            </div>
            <div className="bg-purple-50 rounded-lg p-3 text-center">
              <div className="text-2xl font-bold text-purple-600">20+</div>
              <div className="text-xs text-purple-800">API Endpoints</div>
            </div>
          </div>
        </div>
      </div>

      {/* Role-specific guidance */}
      {user && (
        <div className="mt-8 bg-gray-50 rounded-lg p-6">
          <h3 className="font-semibold mb-3 flex items-center gap-2">
            <Users className="w-5 h-5" />
            Demo Flow for {user.role}
          </h3>
          <div className="text-sm text-gray-600">
            {user.role === 'LawEnforcement' && (
              <p>Focus on: Evidence Upload → Case Association → Chain of Custody → View Evidence → Video Intelligence</p>
            )}
            {user.role === 'Attorney' && (
              <p>Focus on: Evidence Review → Agent Aegis AI → Annotations → Redaction → Court Filing → Discovery → Courtroom Presentation</p>
            )}
            {user.role === 'CaseManager' && (
              <p>Focus on: Evidence Intake → Approval Queue → Disposition → Legal Holds → Compliance → Audit Logs</p>
            )}
            {user.role === 'SystemAdmin' && (
              <p>Focus on: Security & Compliance → AI Agents → Architecture Diagrams → Audit Trail → Demo Reset</p>
            )}
            {user.role === 'PublicSubmitter' && (
              <p>Focus on: Public Evidence Submission → My Cases → Track Submission Status</p>
            )}
          </div>
        </div>
      )}

      {user?.role === 'SystemAdmin' && (
        <div className="space-y-4 mt-4">

          {/* AI Backfill card */}
          <div className="bg-white rounded-xl shadow-sm border border-violet-100 p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-violet-100 rounded-lg">
                <Brain className="w-5 h-5 text-violet-600" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900">AI Evidence Backfill</h3>
                <p className="text-sm text-gray-500">Run Content Understanding on all unprocessed evidence to populate AI Search index for Agent Aegis</p>
              </div>
            </div>

            {backfillState === 'idle' && (
              <button
                onClick={handleBackfill}
                className="px-4 py-2 bg-violet-50 border border-violet-200 text-violet-700 text-sm font-medium rounded-lg hover:bg-violet-100 transition-colors"
              >
                Run AI Backfill
              </button>
            )}

            {backfillState === 'loading' && (
              <div className="flex items-center gap-3 text-gray-600 text-sm">
                <div className="w-5 h-5 border-2 border-gray-300 border-t-violet-600 rounded-full animate-spin" />
                Processing evidence through Content Understanding… this may take a few minutes
              </div>
            )}

            {backfillState === 'success' && backfillResult && (
              <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                <div className="flex items-center gap-2 text-green-700 font-medium text-sm mb-1">
                  <CheckCircle className="w-4 h-4" />
                  Backfill complete
                </div>
                <p className="text-xs text-green-600">
                  {backfillResult.total} total · {backfillResult.processed} processed · {backfillResult.failed} failed
                </p>
                <button onClick={() => { setBackfillState('idle'); setBackfillResult(null); }} className="mt-2 text-xs text-green-700 underline hover:no-underline">Dismiss</button>
              </div>
            )}

            {backfillState === 'error' && (
              <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-sm text-red-700 font-medium">Backfill failed. Check function logs.</p>
                <button onClick={() => setBackfillState('idle')} className="mt-2 text-xs text-red-600 underline hover:no-underline">Dismiss</button>
              </div>
            )}
          </div>

          {/* Reset Demo Data card */}
          <div className="bg-white rounded-xl shadow-sm border border-red-100 p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 bg-red-100 rounded-lg">
              <RotateCcw className="w-5 h-5 text-red-600" />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900">Reset Demo Data</h3>
              <p className="text-sm text-gray-500">Restore all cases, evidence, and custody events to the original demo state</p>
            </div>
          </div>

          {resetState === 'idle' && (
            <button
              onClick={() => setResetState('confirm')}
              className="px-4 py-2 bg-red-50 border border-red-200 text-red-700 text-sm font-medium rounded-lg hover:bg-red-100 transition-colors"
            >
              Reset Demo Data
            </button>
          )}

          {resetState === 'confirm' && (
            <div className="flex items-start gap-3 p-4 bg-amber-50 border border-amber-200 rounded-lg">
              <AlertTriangle className="w-5 h-5 text-amber-500 mt-0.5 flex-shrink-0" />
              <div className="flex-1">
                <p className="text-sm font-medium text-amber-900">This will wipe all current cases and evidence data (including anything added during the demo) and restore the original seed records. This cannot be undone.</p>
                <div className="flex gap-2 mt-3">
                  <button
                    onClick={handleDemoReset}
                    className="px-4 py-1.5 bg-red-600 text-white text-sm font-medium rounded-lg hover:bg-red-700 transition-colors"
                  >
                    Yes, Reset Now
                  </button>
                  <button
                    onClick={() => setResetState('idle')}
                    className="px-4 py-1.5 bg-white border border-gray-300 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          )}

          {resetState === 'loading' && (
            <div className="flex items-center gap-3 text-gray-600 text-sm">
              <div className="w-5 h-5 border-2 border-gray-300 border-t-red-600 rounded-full animate-spin" />
              Resetting demo data…
            </div>
          )}

          {resetState === 'success' && resetResult && (
            <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
              <div className="flex items-center gap-2 text-green-700 font-medium text-sm mb-1">
                <CheckCircle className="w-4 h-4" />
                {resetResult.message}
              </div>
              {resetResult.counts && (
                <p className="text-xs text-green-600">
                  Seeded: {resetResult.counts.cases} cases · {resetResult.counts.evidence} evidence items · {resetResult.counts.custodyEvents} custody chain events
                </p>
              )}
              <button
                onClick={() => { setResetState('idle'); setResetResult(null); }}
                className="mt-3 text-xs text-green-700 underline hover:no-underline"
              >
                Dismiss
              </button>
            </div>
          )}

          {resetState === 'error' && resetResult && (
            <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-700 font-medium">{resetResult.message}</p>
              <button
                onClick={() => { setResetState('idle'); setResetResult(null); }}
                className="mt-2 text-xs text-red-600 underline hover:no-underline"
              >
                Dismiss
              </button>
            </div>
          )}
          </div>
        </div>
      )}
    </div>
  );
}
